import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFileText, FiDownload, FiExternalLink } from 'react-icons/fi';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import MathBackground from '../components/layout/MathBackground';
import { API_BASE_URL } from '../utils/api';

const StudyMaterials = () => {
    const [materials, setMaterials] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [filteredMaterials, setFilteredMaterials] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (selectedCategory === 'All') {
            setFilteredMaterials(materials);
        } else {
            const category = categories.find(cat => cat.name === selectedCategory);
            if (category) {
                setFilteredMaterials(materials.filter(mat => mat.category_id === category.id));
            }
        }
    }, [selectedCategory, materials, categories]);

    const fetchData = async () => {
        try {
            const [categoriesRes, materialsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/study_materials/get_categories.php`),
                fetch(`${API_BASE_URL}/study_materials/get_materials.php`)
            ]);

            const categoriesData = await categoriesRes.json();
            const materialsData = await materialsRes.json();

            if (categoriesData.status === 'success') {
                setCategories(categoriesData.data);
            }

            if (materialsData.status === 'success') {
                setMaterials(materialsData.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = (material) => {
        if (material.external_link) {
            window.open(material.external_link, '_blank');
        } else if (material.file_url) {
            window.open(`${API_BASE_URL}/${material.file_url}`, '_blank');
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const mb = bytes / (1024 * 1024);
        return mb.toFixed(2) + ' MB';
    };

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white font-sans selection:bg-blue-500/30">
            <MathBackground />
            <Navbar />

            <div className="pt-32 pb-20 px-6 max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-neutral-500 mb-6 tracking-tight">
                        Study Materials
                    </h1>
                    <p className="text-neutral-400 max-w-2xl mx-auto text-lg leading-relaxed">
                        Access comprehensive study materials including past papers, revision notes, and worksheets to excel in your mathematics journey.
                    </p>
                </motion.div>

                {/* Category Filter */}
                <div className="flex flex-wrap justify-center gap-4 mb-12">
                    <button
                        onClick={() => setSelectedCategory('All')}
                        className={`px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all border ${selectedCategory === 'All'
                            ? 'bg-blue-600 text-white border-blue-500 shadow-lg shadow-blue-500/25'
                            : 'bg-white/5 text-neutral-400 border-white/10 hover:bg-white/10 hover:text-white'
                            }`}
                    >
                        All
                    </button>
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            onClick={() => setSelectedCategory(category.name)}
                            className={`px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all border ${selectedCategory === category.name
                                ? 'bg-blue-600 text-white border-blue-500 shadow-lg shadow-blue-500/25'
                                : 'bg-white/5 text-neutral-400 border-white/10 hover:bg-white/10 hover:text-white'
                                }`}
                        >
                            {category.name}
                        </button>
                    ))}
                </div>

                {/* Materials Grid */}
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : filteredMaterials.length === 0 ? (
                    <div className="text-center py-20">
                        <FiFileText className="text-6xl text-neutral-700 mx-auto mb-4" />
                        <p className="text-neutral-500 text-lg">No study materials available yet.</p>
                    </div>
                ) : (
                    <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <AnimatePresence>
                            {filteredMaterials.map((material) => (
                                <motion.div
                                    layout
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ duration: 0.3 }}
                                    key={material.id}
                                    className={`group relative backdrop-blur-sm border rounded-2xl p-6 transition-all duration-300 hover:shadow-xl ${material.is_seminar
                                        ? `bg-${material.color || 'blue'}-900/20 border-${material.color || 'blue'}-500/50 shadow-${material.color || 'blue'}-500/20 hover:border-${material.color || 'blue'}-400 hover:shadow-${material.color || 'blue'}-500/40`
                                        : 'bg-white/[0.02] border-white/5 hover:border-blue-500/30 hover:shadow-blue-500/10'
                                        }`}
                                >
                                    {/* PDF Icon / Seminar Badge */}
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className={`p-4 rounded-xl border ${material.is_seminar
                                            ? `bg-${material.color || 'blue'}-600/20 border-${material.color || 'blue'}-500/40 shadow-inner shadow-${material.color || 'blue'}-500/20 animate-pulse`
                                            : 'bg-blue-600/10 border-blue-500/20'
                                            }`}>
                                            <FiFileText className={`text-3xl ${material.is_seminar ? `text-${material.color || 'blue'}-400` : 'text-blue-500'}`} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-start justify-between gap-2">
                                                <h3 className="text-lg font-bold text-white mb-1 line-clamp-2">
                                                    {material.title}
                                                </h3>
                                                {material.is_seminar && (
                                                    <span className={`flex-shrink-0 px-2.5 py-1 bg-${material.color || 'blue'}-500/20 text-${material.color || 'blue'}-300 text-[10px] font-black uppercase tracking-widest rounded-full border border-${material.color || 'blue'}-500/30 shadow-[0_0_10px_rgba(0,0,0,0.5)] shadow-${material.color || 'blue'}-500/30 flex items-center gap-1.5`}>
                                                        <span className={`w-1.5 h-1.5 rounded-full bg-${material.color || 'blue'}-400 animate-ping absolute`}></span>
                                                        <span className={`w-1.5 h-1.5 rounded-full bg-${material.color || 'blue'}-400 relative`}></span>
                                                        Live Session
                                                    </span>
                                                )}
                                            </div>
                                            <span className="inline-block px-3 py-1 bg-white/5 text-blue-400 text-xs font-bold uppercase tracking-wider rounded-full border border-white/10 mt-1">
                                                {material.category_name}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    {material.description && (
                                        <p className="text-neutral-400 text-sm mb-4 line-clamp-3">
                                            {material.description}
                                        </p>
                                    )}

                                    {/* File Info */}
                                    <div className="flex items-center justify-between text-xs text-neutral-500 mb-4">
                                        <span>{new Date(material.created_at).toLocaleDateString()}</span>
                                        {material.file_size && (
                                            <span>{formatFileSize(material.file_size)}</span>
                                        )}
                                    </div>

                                    {/* Download Button */}
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleDownload(material)}
                                        className={`w-full py-3 text-white font-bold text-sm rounded-xl transition-all flex items-center justify-center gap-2 ${material.is_seminar
                                            ? `bg-${material.color || 'blue'}-600 hover:bg-${material.color || 'blue'}-500 shadow-lg shadow-${material.color || 'blue'}-500/30`
                                            : 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20'
                                            }`}
                                    >
                                        {material.external_link ? (
                                            <>
                                                <FiExternalLink /> {material.is_seminar ? 'Join Live Session' : 'Open Link'}
                                            </>
                                        ) : (
                                            <>
                                                <FiDownload /> Download PDF
                                            </>
                                        )}
                                    </motion.button>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default StudyMaterials;
